import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Pagination, Spinner, Alert, Badge, Row, Col, Container, Form ,Button} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from '../services/apiService';
import Breadcrumb from './Breadcrumb';

// Function to format address from property data
const formatAddress = (propertyData) => {
    if (!propertyData || !Array.isArray(propertyData) || propertyData.length === 0) {
        return 'No address available';
    }
    
    const property = propertyData[0]; // Get first property
    const { address1, address2, city, state, zipcode } = property;
    
    let formattedAddress = '';
    if (address1) formattedAddress += address1;
    if (address2) formattedAddress += `, ${address2}`;
    if (city) formattedAddress += formattedAddress ? `, ${city}` : city;
    if (state) formattedAddress += `, ${state}`;
    if (zipcode) formattedAddress += ` ${zipcode}`;
    
    return formattedAddress || 'Address not available';
};

// Function to format datetime
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return dateString;
    }
};

// Get status display name
const getStatusDisplayName = (completionStatus) => {
    if (!completionStatus) {
        return 'All Failed Automations';
    }
    const statusMap = {
        'both_agents_present': 'Both Agents Present',
        'listing_agents_only': 'Listing Agents Only',
        'selling_agents_only': 'Selling Agents Only',
        'fsbo_no_agents': 'FSBO - No Agents',
        'agent_copy_missing': 'Agent Copy Missing',
        'both_agents_missing': 'Both Agents Missing',
        'no_completion_status': 'No Completion Status'
    };
    return statusMap[completionStatus] || completionStatus;
};

// Get status badge variant
const getStatusBadgeVariant = (completionStatus) => {
    switch (completionStatus) {
        case 'both_agents_present':
            return 'success';
        case 'listing_agents_only':
        case 'selling_agents_only':
            return 'primary';
        case 'fsbo_no_agents':
            return 'info';
        case 'agent_copy_missing':
        case 'both_agents_missing':
            return 'warning';
        default:
            return 'secondary';
    }
};

const FilteredAutomationList = ({ 
    completionStatus, 
    title, 
    subtitle, 
    breadcrumbItems,
    status = null  // New prop for filtering by status (e.g., 'failed')
}) => {
    const navigate = useNavigate();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRuns, setTotalRuns] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const pageSize = 10;

    // Filter options similar to FailedAutomation.js
    const filterOptions = [
        { value: 'all', label: 'All Failed Automations', description: 'All failed runs' },
        { value: 'listing_agents_only', label: 'Listing Agents Only', description: 'Listing agents only' },
        { value: 'selling_agents_only', label: 'Selling Agents Only', description: 'Selling agents only' },
        { value: 'fsbo_no_agents', label: 'FSBO - No Agents', description: 'FSBO with no agents' },
        { value: 'agent_copy_missing', label: 'Agent Copy Missing', description: 'Missing agent copies' },
        { value: 'both_agents_missing', label: 'Both Agents Missing', description: 'Both agents missing' },
        { value: 'no_completion_status', label: 'Failed Unexpected', description: 'No completion status' }
    ];

    // Handle filter change
    const handleFilterChange = (filterValue) => {
        setSelectedFilter(filterValue);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    // Function to fetch filtered automation runs
    const fetchFilteredAutomationRuns = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            // Determine the completion status based on selectedFilter
            let statusFilter = completionStatus;
            if (selectedFilter !== 'all') {
                statusFilter = selectedFilter;
            }
            
            const data = await apiService.getFilteredAutomationRuns(statusFilter, page, pageSize, status);
            setRuns(data.results);
            setTotalRuns(data.count);
            setTotalPages(data.num_pages);
            setHasNext(data.has_next);
            setHasPrevious(data.has_previous);
        } catch (err) {
            setError('Failed to fetch automation runs.');
            console.error('Error fetching filtered automation runs:', err);
        } finally {
            setLoading(false);
        }
    }, [completionStatus, selectedFilter, pageSize, status]);

    // Initial data fetch
    useEffect(() => {
        fetchFilteredAutomationRuns(currentPage);
    }, [currentPage, fetchFilteredAutomationRuns]);

    // Refetch data when filter changes
    useEffect(() => {
        fetchFilteredAutomationRuns(1);
        setCurrentPage(1);
    }, [selectedFilter, fetchFilteredAutomationRuns]);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle row click to navigate to logs page
    const handleRowClick = (automationRunId) => {
        navigate(`/automation/logs/${automationRunId}`);
    };

    return (
        <Container fluid className="px-0" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
            
            <style jsx>{`
                .page-title {
                    color: #2c3e50;
                    font-weight: 700;
                    font-size: 2rem;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                
                .page-subtitle {
                    font-size: 1rem;
                    color: #6c757d;
                }
                
                .stats-summary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    color: white;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }
                
                .main-card {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                
                .card-header-custom {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 1.5rem 2rem;
                }
                
                .table-modern {
                    width: 100%;
                    margin-bottom: 0;
                    table-layout: auto;
                }
                
                .table-modern thead th {
                    background-color: #f8f9fa;
                    border: none;
                    font-weight: 600;
                    color: #495057;
                    padding: 12px 8px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    white-space: nowrap;
                    border-bottom: 2px solid #e9ecef;
                }
                
                .table-modern thead th:nth-child(1) { min-width: 80px; }  /* ID */
                .table-modern thead th:nth-child(2) { min-width: 110px; } /* Name */
                .table-modern thead th:nth-child(3) { min-width: 200px; } /* Address */
                .table-modern thead th:nth-child(4) { min-width: 80px; } /* Started */
                .table-modern thead th:nth-child(5) { min-width: 80px; } /* Completed */
                .table-modern thead th:nth-child(6) { min-width: 40px; } /* Duration */
                .table-modern thead th:nth-child(7) { min-width: 300px; } /* Error */
                
                /* Enhanced Filter Animations */
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes pulse {
                    0% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                    50% {
                        box-shadow: 0 6px 20px rgba(23, 162, 184, 0.5);
                    }
                    100% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .filter-card {
                    animation: slideDown 0.3s ease-out;
                }
                
                .filter-controls {
                    animation: fadeInUp 0.4s ease-out 0.1s both;
                }
                
                .filter-info {
                    animation: fadeInUp 0.4s ease-out 0.2s both;
                }
                
                .filter-stats {
                    animation: fadeInUp 0.4s ease-out 0.3s both;
                }
                
                .stat-item {
                    transition: all 0.3s ease;
                    padding: 0.5rem;
                    border-radius: 8px;
                }
                
                .stat-item:hover {
                    background-color: rgba(0,0,0,0.05);
                    transform: translateY(-2px);
                }
                
                .form-select-modern:hover {
                    border-color: #667eea !important;
                    transform: translateY(-1px);
                }
                
                .info-badge {
                    transition: all 0.3s ease;
                }
                
                .info-badge:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(23, 162, 184, 0.4) !important;
                }
                
                .btn-group .btn {
                    transition: all 0.3s ease;
                }
                
                .btn-group .btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .table-modern tbody tr {
                    border: none;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .table-modern tbody tr:hover {
                    background-color: #f8f9fa;
                    transform: translateX(3px);
                    box-shadow: 0 3px 10px rgba(0,0,0,0.08);
                }
                
                .table-modern tbody td {
                    border: none;
                    padding: 12px 8px;
                    vertical-align: middle;
                    border-bottom: 1px solid #e9ecef;
                    font-size: 0.85rem;
                    letter-spacing: 0.2px;
                }
                
                .loading-container {
                    padding: 4rem 2rem;
                    text-align: center;
                }
                
                .loading-spinner {
                    width: 3rem;
                    height: 3rem;
                    border-width: 0.3rem;
                }
                
                .empty-state {
                    padding: 4rem 2rem;
                    text-align: center;
                    color: #6c757d;
                }
                
                .empty-state-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .pagination-container {
                    background-color: #f8f9fa;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e9ecef;
                }
                
                .pagination-info {
                    color: #6c757d;
                    font-weight: 500;
                }
                
                
                
                .name-cell {
                    // font-weight: 600;
                    color: #2d3748;
                }
                
                .date-cell {
                    color: #4a5568;
                    font-size: 0.75rem;
                    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
                    white-space: nowrap;
                }
                
                .duration-badge {
                    background-color: #e9ecef;
                    color: #495057;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .error-tooltip {
                    max-width: 400px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    background-color: #fff5f5;
                    color: #c53030;
                    padding: 0.5rem 0.75rem;
                    border-radius: 8px;
                    border-left: 3px solid #fc8181;
                    font-size: 0.75rem;
                    line-height: 1.4;
                    cursor: help;
                    transition: all 0.2s ease;
                }
                
                .error-tooltip:hover {
                    background-color: #fed7d7;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(197, 48, 48, 0.15);
                }
            `}</style>

            {/* Page Header Section */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="page-title mb-1">{title || getStatusDisplayName(completionStatus)}</h2>
                            <p className="page-subtitle text-muted mb-0">{subtitle || 'Automation runs filtered by completion status'}</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            
                            <div className="btn-group" role="group">
                                <Button 
          variant={showFilters ? "primary" : "outline-primary"}
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center gap-2"
        >
          <i className="fas fa-filter"></i>
          {showFilters ? 'Filters' : 'Filters'}
        </Button>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Enhanced Professional Filter Section */}
            {showFilters && (
                <Card 
                    className="mb-4 filter-card" 
                    style={{ 
                        border: 'none', 
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        animation: 'slideDown 0.3s ease-out'
                    }}
                >
                    <Card.Header 
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px 16px 0 0',
                            padding: '1rem 1.5rem'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                                <h6 className="mb-0 fw-bold">Filters</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                
                                <button 
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setShowFilters(false)}
                                    style={{ borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
                        <Row className="align-items-center g-4 mb-2">
                            <Col lg={3}>
                                <div className="filter-controls">
                                    <label className="form-label fw-semibold text-muted mb-2">
                                        Failed Automation
                                    </label>
                                    <div className="position-relative">
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={(e) => handleFilterChange(e.target.value)}
                                            className="form-select-modern"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                padding: '0.75rem 1rem',
                                                background: 'white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e9ecef';
                                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                                            }}
                                        >
                                            {filterOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <i className="fas fa-chevron-down position-absolute" 
                                           style={{
                                               right: '1rem',
                                               top: '50%',
                                               transform: 'translateY(-50%)',
                                               pointerEvents: 'none',
                                               color: '#6c757d',
                                               fontSize: '0.8rem'
                                           }}>
                                        </i>
                                    </div>
                                </div>
                            </Col>
                            
                        </Row>
                        
                        {/* Filter Statistics */}
                        <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
                            <Col>
                                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                                  
                                    <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                                        <button 
                                            className="btn btn-outline-primary btn-sm me-2"
                                            onClick={() => {
                                                setSelectedFilter('all');
                                                setCurrentPage(1);
                                            }}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-refresh me-1"></i>
                                            Reset
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => fetchFilteredAutomationRuns(1)}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-sync-alt me-1"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Statistics Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Total {filterOptions.find(opt => opt.value === selectedFilter)?.label}
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {totalRuns}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-play-circle fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                
               
            </Row>
            {/* Main Content Card */}
            <Card className="main-card">
                <Card.Header className="card-header-custom">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 fw-bold">{filterOptions.find(opt => opt.value === selectedFilter)?.label}</h5>
                        </Col>
                        
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-0">
                    {loading && (
                        <div className="loading-container">
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                className="loading-spinner mb-3"
                            />
                            <h5 className="text-muted">Loading automation runs...</h5>
                            <p className="text-muted mb-0">Please wait while we fetch the latest data</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="p-4">
                            <Alert variant="danger" className="border-0 rounded-3">
                                <Alert.Heading className="h6">⚠️ Error Loading Data</Alert.Heading>
                                {error}
                            </Alert>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {runs.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="table-modern mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>Started</th>
                                                <th>Duration</th>
                                                <th>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {runs.map((run) => (
                                                <tr 
                                                    key={run.id}
                                                    onClick={() => handleRowClick(run.id)}
                                                >
                                                    <td>
                                                        <span className="id-badgess">#{run.id}</span>
                                                    </td>
                                                    <td className="name-cells" title={ run.orderNumber}>
                                                        {
                                                            run.orderNumber
                                                        }
                                                    </td>
                                                    <td className="address-cell" title={formatAddress(run.properties)}>
                                                        {formatAddress(run.properties)}
                                                    </td>
                                                    <td className="date-cell" title={formatDateTime(run.startedAt || run.createdAt)}>
                                                        <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                                            {formatDateTime(run.startedAt || run.createdAt)}
                                                        </div>
                                                    </td>
                                                    
                                                    <td>
                                                        {run.duration ? (
                                                            <span className="duration-badge">
                                                                {run.duration.toFixed(1)}s
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {run.error ? (
                                                            <div className="error-tooltip" style={{cursor:"pointer"}} title={run.error}>
                                                                {run.error.length > 50 ? `${run.error.substring(0, 50)}...` : run.error}
                                                            </div>
                                                        ) : (
                                                            <span className="text-success" style={{ fontSize: '0.8rem' }}>✓ OK</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">📋</div>
                                    <h5 className="mb-2">No Records Found</h5>
                                    <p className="mb-0">No automation runs found for the selected filter</p>
                                </div>
                            )}
                            
                            {runs.length > 0 && totalPages > 1 && (
                                <div className="pagination-container">
                                    <Row className="align-items-center">
                                        <Col>
                                            <div className="pagination-info">
                                                Showing page {currentPage} of {totalPages} ({totalRuns} total records)
                                            </div>
                                        </Col>
                                        <Col xs="auto">
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={!hasPrevious}
                                                />
                                                <Pagination.Prev 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={!hasPrevious}
                                                />
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <Pagination.Item 
                                                            key={pageNum} 
                                                            active={pageNum === currentPage}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={!hasNext}
                                                />
                                                <Pagination.Last 
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={!hasNext}
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default FilteredAutomationList;